<script setup lang="ts">
import { computed, ref } from 'vue'
import { useFormattedDate } from '../composables/useFormattedDate'
import { useNow } from '../composables/useNow'
import { shortThread } from '../composables/shortThread'
import { agentCostLabel } from './AgentCostLabel'
import type { AgentSessionSummary } from '../stores/agent/types'

// One row of the /agent Sessions column: a status dot, the session title and
// a meta line, plus the per-row actions (info / rename / stop / delete).
// Rename and delete-confirm are inline so the list needs no dialog provider;
// the parent owns the actual store mutations via emits. On desktop the
// actions float over the row's right edge on hover or keyboard focus; on
// phones they sit in their own row with 44px targets.
const props = defineProps<{
  session: AgentSessionSummary
  active: boolean
  // This row's stop (~10s of synchronous teardown) is in flight (#1381).
  stopping: boolean
}>()

const emit = defineEmits<{
  open: [threadId: string]
  rename: [threadId: string, name: string]
  delete: [threadId: string]
  stop: [threadId: string]
  info: [session: AgentSessionSummary]
}>()

const { formatDate, formatAbsoluteDate } = useFormattedDate()
const now = useNow()

const label = computed(() => props.session.name || shortThread(props.session.thread_id))

const editing = ref(false)
const editValue = ref('')
const confirming = ref(false)

function startRename() {
  confirming.value = false
  editing.value = true
  editValue.value = props.session.name ?? ''
}

function commitRename() {
  if (!editing.value) return
  editing.value = false
  const name = editValue.value.trim()
  // Ignore an empty rename — keep the existing name rather than clearing it.
  if (name) emit('rename', props.session.thread_id, name)
}

function startDelete() {
  editing.value = false
  confirming.value = true
}

function confirmDelete() {
  confirming.value = false
  emit('delete', props.session.thread_id)
}

const ICON_BUTTON =
  'inline-flex size-8 max-sm:size-11 items-center justify-center rounded-md text-ink-muted hover:bg-surface-raised hover:text-ink disabled:cursor-not-allowed disabled:opacity-50'
</script>

<template>
  <li class="group relative rounded-lg" :class="active ? 'bg-surface-hover' : ''">
    <input
      v-if="editing"
      :ref="
        (el) => {
          if (el) (el as HTMLInputElement).focus()
        }
      "
      v-model="editValue"
      :aria-label="`New name for ${label}`"
      class="min-h-11 w-full rounded-lg border border-border-control bg-surface-bg px-2.5 text-sm text-ink outline-none focus:border-brand-primary"
      data-testid="agent-session-rename-input"
      @keydown.enter.prevent="commitRename"
      @keydown.esc.prevent="editing = false"
      @blur="commitRename"
    />
    <button
      v-else
      type="button"
      class="flex min-h-11 w-full min-w-0 items-start gap-2.5 rounded-lg px-2.5 py-2 text-left"
      :class="active ? 'text-ink' : 'text-ink-muted hover:bg-surface-hover hover:text-ink'"
      :aria-current="active ? 'true' : undefined"
      data-testid="agent-session-item"
      @click="emit('open', session.thread_id)"
    >
      <span
        v-if="session.running"
        class="mt-1.5 size-2 shrink-0 animate-pulse rounded-full bg-factory"
        data-testid="agent-session-running"
        aria-hidden="true"
      />
      <span v-else class="mt-1.5 size-2 shrink-0 rounded-full bg-ink-faint/60" aria-hidden="true" />
      <span class="flex min-w-0 flex-1 flex-col gap-0.5">
        <span class="truncate text-sm" data-testid="agent-session-name">{{ label }}</span>
        <span class="flex flex-wrap items-center gap-x-1.5 text-xs text-ink-faint">
          <span :class="session.running ? 'text-factory' : ''">{{ session.running ? 'Running' : 'Idle' }}</span>
          <template v-if="session.total_cost_cents != null">
            <span aria-hidden="true">·</span>
            <span class="tabular-nums" title="Estimated total cost for this session" data-testid="agent-session-cost">{{
              agentCostLabel(session.total_cost_cents)
            }}</span>
          </template>
          <template v-if="session.last_activity_at">
            <span aria-hidden="true">·</span>
            <span
              class="tabular-nums"
              :title="formatAbsoluteDate(session.last_activity_at)"
              data-testid="agent-session-time"
              >{{ formatDate(session.last_activity_at, now) }}</span
            >
          </template>
        </span>
      </span>
    </button>

    <div
      v-if="!editing"
      class="flex items-center gap-0.5 max-sm:justify-end max-sm:px-1 max-sm:pb-1 sm:absolute sm:top-1.5 sm:right-1 sm:rounded-md sm:bg-surface-hover sm:group-focus-within:opacity-100 sm:group-hover:opacity-100"
      :class="confirming ? 'sm:opacity-100' : 'sm:opacity-0'"
    >
      <template v-if="confirming">
        <button
          type="button"
          class="min-h-8 rounded-md border border-status-alert/60 px-2.5 text-xs font-medium text-status-alert hover:bg-status-alert/10 max-sm:min-h-11"
          :aria-label="`Confirm delete session ${label}`"
          data-testid="agent-session-delete-confirm"
          @click="confirmDelete"
        >
          Delete?
        </button>
        <button
          type="button"
          :class="ICON_BUTTON"
          :aria-label="`Cancel deleting session ${label}`"
          @click="confirming = false"
        >
          <span aria-hidden="true">✕</span>
        </button>
      </template>
      <template v-else>
        <button
          type="button"
          :class="ICON_BUTTON"
          :aria-label="`Devcontainer info for ${label}`"
          data-testid="agent-session-info"
          @click="emit('info', session)"
        >
          <span aria-hidden="true">ⓘ</span>
        </button>
        <button
          type="button"
          :class="ICON_BUTTON"
          :aria-label="`Rename session ${label}`"
          data-testid="agent-session-rename"
          @click="startRename"
        >
          <span aria-hidden="true">✎</span>
        </button>
        <button
          type="button"
          :class="ICON_BUTTON"
          :disabled="stopping"
          :aria-busy="stopping ? 'true' : undefined"
          title="Stop this session's container — its slot frees up, but the conversation stays"
          :aria-label="`Stop session ${label}`"
          data-testid="agent-session-stop"
          @click="emit('stop', session.thread_id)"
        >
          <span aria-hidden="true" :class="stopping ? 'animate-pulse' : ''">⏸</span>
        </button>
        <button
          type="button"
          :class="ICON_BUTTON"
          :aria-label="`Delete session ${label}`"
          data-testid="agent-session-delete"
          @click="startDelete"
        >
          <span aria-hidden="true">✕</span>
        </button>
      </template>
    </div>
  </li>
</template>
